import cn from 'classnames';
import minBy from 'lodash/minBy';

import type { TCabinCategory } from '@/helpers/api/graphql/queries/cabinCategoriesAvailability';
import type { TCurrencyCode } from '@/types';

import CabinPrice from '@/components/CabinCard/CabinPrice';
import * as Dialog from '@/components/elements/Dialog';
import UIResource from '@/components/UIResource';
import UIResourceImage from '@/components/UIResource/Image';
import { selectAccessible, selectSailors } from '@/ducks/filters/selectors';
import useStrikethrough from '@/ducks/programs/strikethrough/useStrikethrough';
import { useAppSelector } from '@/store';

import VerificationModal from '../VerificationDialog';
import ModuleCardSkeleton from './Skeleton';

import styles from './ModuleCard.module.scss';

type TModuleCardProps = {
  availableUnits?: TCabinCategory[];
  cabins: number;
  currencyCode: TCurrencyCode;
  duration?: number;
  isLoading: boolean;
  isVip?: boolean;
  priceType: string;
};

const listFormatter = new Intl.ListFormat('en', {
  style: 'long',
  type: 'conjunction',
});

const PRICE_TYPE_COPY: Record<string, string> = {
  perCabin: 'Per Cabin',
  perSailor: 'Per Sailor',
  SailorPerNight: 'Per Sailor Per Night',
};

const ModuleCard = ({
  availableUnits = [],
  cabins,
  currencyCode,
  duration = 1,
  isLoading,
  isVip,
  priceType,
}: TModuleCardProps) => {
  const getStrikethroughForPackage = useStrikethrough();

  const sailors = useAppSelector(selectSailors);
  const isAccessible = useAppSelector(selectAccessible);

  const availableUnitsList = listFormatter.format(availableUnits?.map?.((unit) => unit.name));
  const isAvailable = availableUnits.length > 0;
  const cheapestUnit = minBy(availableUnits, 'lowestSubCategoryPrice.totalPrice.amount');
  const strikethroughValue = getStrikethroughForPackage(cheapestUnit?.lowestSubCategoryPrice?.subCategoryCode);

  const subKey = isVip ? 'suite' : 'cabin';

  return (
    <Dialog.Root>
      {isLoading ? (
        <ModuleCardSkeleton />
      ) : (
        <Dialog.Trigger className={cn(styles.root, !isAvailable && styles.unavailable)}>
          <div className={styles.preview}>
            <UIResourceImage
              className={styles.previewImage}
              resourceId={`CabinDescriptor.${subKey}.image`}
              role="presentation"
            />

            <span className={styles.heading}>
              <UIResource id={`CabinDescriptor.${subKey}.heading`} />
            </span>
          </div>

          <div className={styles.content}>
            {!isAvailable && (
              <span>
                <UIResource id="CabinDescriptor.unavailable.label" />
              </span>
            )}

            {isAvailable && (
              <>
                <div className={styles.pricing}>
                  <span>
                    <UIResource id="CabinDescriptor.pricesFrom.label" />
                  </span>
                  <div className={styles.pricingAmount}>
                    <CabinPrice
                      abTestPriceType={priceType}
                      cabins={cabins}
                      currencyCode={currencyCode}
                      duration={duration}
                      hideOriginalAmount={false}
                      sailors={sailors}
                      startingPrice={cheapestUnit?.lowestSubCategoryPrice?.totalPrice}
                      strikethroughValue={strikethroughValue}
                    />
                    <span>{PRICE_TYPE_COPY[priceType]} (incl. Taxes &amp; Fees)</span>
                  </div>
                </div>
                <p className={styles.availableCategories}>
                  <UIResource
                    id={isVip ? 'CabinDescriptor.suites.availability' : 'CabinDescriptor.cabins.availability'}
                    values={{
                      availableCategories: <span className={styles.bold}>{availableUnitsList}</span>,
                    }}
                  />
                </p>
              </>
            )}
          </div>
        </Dialog.Trigger>
      )}
      <VerificationModal accessible={isAccessible} sailors={sailors} />
    </Dialog.Root>
  );
};

ModuleCard.displayName = 'ModuleCard';

export default ModuleCard;
